import { Component, computed, effect, inject, input, output, QueryList, Signal, ViewChildren } from '@angular/core';
import { BoFormsModule } from '@bo/ng-forms';
import { TransactionItem, TransactionItemError, VatType } from '../../../../../../_interfaces/transactions.interface';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { maskConfig } from '../../../../../../shared/configs/configs';
import { NgxCurrencyDirective } from '@dintecom/ngx-currency';
import { defaultVatType, vatOptions } from '../../_utils/transactions-utils';
import { TrackingCategoriesComponent } from '../../_components/tracking-categories/tracking-categories.component';
import { AccountingService } from '../../../_services/accounting/accounting.service';
import { TaxRateSelectComponent } from '../../_components/tax-rate-select/tax-rate-select.component';
import {
  AccountingConnectionStatus,
  AccountingProviderCompany,
  AccountingTaxRates,
} from '../../../../../../_interfaces/accounting.interface';

@Component({
  selector: 'app-transaction-split-item',
  imports: [
    BoFormsModule,
    NgSelectModule,
    NgbTooltip,
    NgxCurrencyDirective,
    TrackingCategoriesComponent,
    TaxRateSelectComponent,
  ],
  templateUrl: './transaction-split-item.component.html',
  styleUrl: './transaction-split-item.component.scss',
})
export class TransactionSplitItemComponent {
  @ViewChildren(NgbTooltip) tooltips!: QueryList<NgbTooltip>;
  accountingService = inject(AccountingService);

  // Inputs
  item = input.required<TransactionItem>();
  itemIndex = input.required<number>();
  preferredTaxRateId = input<number | undefined>(undefined);
  itemId = input.required<string>(); // Unique tracking ID for this item
  itemError = input<TransactionItemError | null>(null); // Error object for this item from API

  // Outputs
  itemChange = output<TransactionItem>();
  clearFieldError = output<{ field: string }>(); // Emit when a user changes a field with an error

  trackingCategories = this.accountingService.trackingCategories;
  taxRates = this.accountingService.taxRates;
  readonly provider = computed(() => this.accountingService.provider());
  protected hasActiveConnection: Signal<boolean> = computed(() => {
    return this.accountingService.connectionStatus() === AccountingConnectionStatus.Complete;
  });

  vatOptions = vatOptions;

  // Local state synced with item input
  protected name: string = '';
  protected amount: string | null = null;
  protected unitPrice: string | null = null;
  protected unitQuantity: number | null = null;
  protected vatType: number = defaultVatType;
  protected selectedTaxRate: number | undefined; // Store only the ID
  protected selectedTracking: number[] = [];

  // Computed: Find the full tax rate object from the selected ID for the component input
  protected selectedTaxRateObject = computed<AccountingTaxRates | undefined>(() => {
    const taxRateId = this.selectedTaxRate;
    if (!taxRateId) return undefined;
    return this.taxRates().find((rate) => rate.id === taxRateId);
  });

  constructor() {
    // Use effect to sync local state with item input signal
    // This runs whenever the item signal changes, including when entering edit mode
    effect(() => {
      const itemData = this.item();
      this.name = itemData.name;
      // this.unitPrice = itemData.unit_price?.toString() ?? null;
      // this.unitQuantity = itemData.unit_quantity ?? null;
      this.amount = itemData.amount;
      this.vatType = itemData.vat ?? defaultVatType; // Use default if vat is undefined
      this.selectedTaxRate = itemData.tax; // Already an ID from TransactionItem
      this.selectedTracking = itemData.tracking ?? [];
    });
  }

  closeAllTooltips() {
    this.tooltips.forEach((tooltip) => tooltip.close());
  }

  updateName(value: string) {
    this.name = value;
    // Clear error for this field when the user starts typing
    if (this.itemError()?.name) {
      this.clearFieldError.emit({ field: 'name' });
    }
    this.closeAllTooltips();
    this.emitChanges();
  }

  onUnitPriceFocus(): void {
    const quantity = Number(this.unitQuantity);
    const amount = Number(this.amount);

    // Only prefill when field is empty and we have both amount and quantity
    if (!this.unitPrice && amount && quantity && quantity !== 0) {
      this.unitPrice = (amount / quantity).toFixed(2);
    }
  }

  updateUnitPrice(value: string | null) {
    this.unitPrice = value;
    const price = Number(this.unitPrice);
    const quantity = Number(this.unitQuantity);

    // If both price and quantity exist, calculate amount = price × quantity
    if (price && quantity && quantity !== 0) {
      this.amount = (price * quantity).toFixed(2);
    }
    this.emitChanges();
  }

  updateUnitQuantity(value: number | null) {
    this.unitQuantity = value;
    const price = Number(this.unitPrice);
    const quantity = Number(this.unitQuantity);

    // If both price and quantity exist, calculate amount = price × quantity
    if (price && quantity && quantity !== 0) {
      this.amount = (price * quantity).toFixed(2);
    }
    this.emitChanges();
  }

  updateAmount(value: string | null) {
    // Convert empty string, "0", or null to null for consistent behavior
    // This ensures cleared fields behave the same as newly added items
    const numericValue = value ? Number.parseFloat(value) : 0;
    if (!value || value === '' || numericValue === 0) {
      this.amount = null;
    } else {
      this.amount = value;
      const unitPrice = this.unitQuantity ? Number(value) / this.unitQuantity : null;
      this.unitPrice = unitPrice?.toString() ?? null;
    }

    // Clear error for this field when user starts typing
    if (this.itemError()?.amount) {
      this.clearFieldError.emit({ field: 'amount' });
    }
    this.closeAllTooltips();
    this.emitChanges();
  }

  updateVatType(value: VatType) {
    this.vatType = value;
    this.emitChanges();
  }

  updateTaxRate(value: number | undefined) {
    this.selectedTaxRate = value;
    this.emitChanges();
  }

  updateTracking(value: number[]) {
    this.selectedTracking = value;
    this.emitChanges();
  }

  emitChanges() {
    this.itemChange.emit({
      category: this.item().category,
      name: this.name,
      // unit_quantity: this.unitQuantity ?? undefined,
      // unit_price: this.unitPrice ?? undefined,
      amount: this.amount,
      vat: this.vatType,
      tax: this.selectedTaxRate,
      tracking: this.selectedTracking,
    });
  }

  protected readonly maskConfig = maskConfig;
  protected readonly AccountingProviderCompany = AccountingProviderCompany;
}
